import React, { useState, useEffect } from "react";
import "./LoginOverlay.css";
import ForgotPassword from "../ForgotPassword/forgotPassword";
import ChangePassword from "../ChangePassword/changePassword";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";

const LoginOverlay = ({ onLoginSuccess }) => {
    const toast = useToast();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [newPasswordRequired, setNewPasswordRequired] = useState(false);
    const [session, setSession] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
 
    useEffect(() => {
        if (newPasswordRequired) setShowChangePassword(true);
    }, [newPasswordRequired]);

    const validateLogin = () => {
        const next = {};
        if (!email.trim()) next.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Enter a valid email address.";
        if (!password) next.password = "Password is required.";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!validateLogin()) return;
        setSubmitting(true);
        try {
            const response = await apiClient.post("/user/login", {
                username: email,
                password: password,
            });
            if (response.data.challengeName === "NEW_PASSWORD_REQUIRED") {
                setNewPasswordRequired(true);
                setSession(response.data.session);
            } else {
                await onLoginSuccess(response.data);
            }
        } catch (err) {
            if (err.response?.data?.error) {
                toast.error(err.response.data.error);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onPasswordChanged = async (tokens) => {
        setShowChangePassword(false);
        setNewPasswordRequired(false);
        try {
            await onLoginSuccess(tokens);
        } catch (err) {
            toast.error(err.response?.data?.error || "Login failed after password change. Please log in manually.");
        }
    };

    return (
        <>
            <div className="login-overlay-backdrop">
                <div className="login-overlay-brand" aria-hidden="true">
                    <img src="/DYC-icon-ColorWheel.png" alt="" className="login-overlay-watermark" />
                </div>
                <div className="login-overlay-card">
                    <form className="login-overlay-form" onSubmit={onSubmit}>
                        <img src="/DYC-icon-ColorWheel.png" alt="DYC Logo" className="login-overlay-logo" />
                        <input
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: undefined })); }}
                            placeholder="Email"
                            className={`login-overlay-input${errors.email ? " input-error" : ""}`}
                            name="username"
                            autoComplete="username"
                        />
                        {errors.email && <span className="login-overlay-field-error">{errors.email}</span>}
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            value={password}
                            className={`login-overlay-input${errors.password ? " input-error" : ""}`}
                            name="password"
                            autoComplete="current-password"
                            onChange={(e) => { setPassword(e.target.value); setErrors((prev) => ({ ...prev, password: undefined })); }}
                        />
                        {errors.password && <span className="login-overlay-field-error">{errors.password}</span>}
                        <div className="login-overlay-show-pw">
                            <input
                                type="checkbox"
                                id="loginOverlayShowPw"
                                checked={showPassword}
                                onChange={() => setShowPassword(!showPassword)}
                            />
                            <label htmlFor="loginOverlayShowPw">Show Password</label>
                        </div>
                        <button type="submit" className="login-overlay-btn" disabled={submitting}>
                            {submitting ? "Signing in…" : "Login"}
                        </button>
                        <div
                            className="login-overlay-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => window.open("/Register", "_blank", "noopener")}
                            onKeyDown={(e) => { if (e.key === "Enter") window.open("/Register", "_blank", "noopener"); }}
                        >
                            New to DYC? Get started
                        </div>
                        <div
                            className="login-overlay-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowForgotPassword(true)}
                            onKeyDown={(e) => { if (e.key === "Enter") setShowForgotPassword(true); }}
                        >
                            Forgot Password?
                        </div>
                    </form>
                </div>
            </div>
            {showForgotPassword && <ForgotPassword setShowForgotPassword={setShowForgotPassword} />}
            {showChangePassword && (
                <ChangePassword
                    setShowChangePassword={setShowChangePassword}
                    email={email}
                    session={session}
                    onPasswordChanged={onPasswordChanged}
                />
            )}
        </>
    );
};

export default LoginOverlay;
